import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { REGIONS, searchAuthoring, type SearchResponse } from "@/api/authoring";
import { useTenantScope } from "@/auth/tenant";
import { AppLayout } from "@/components/AppLayout";
import { TenantField } from "@/components/TenantField";
import { BiomarkerCombobox } from "@/components/BiomarkerCombobox";
import { t } from "@/i18n";

/**
 * Authoring search (phase2-admin-ui lookup slice). Three composable axes:
 *
 * - biomarkers ("GLU" or "GLU, TCH" — all-of by default, any-of switchable):
 *   matches `related_biomarkers` AND condition attributes;
 * - profile predicates ("diabetes", "smoker"): matches condition
 *   attributes/values ("patients with diabetes", "smokers");
 * - free text over authored content bodies, with snippets — and the rule
 *   results narrow to rules firing a matching insight.
 */
export function SearchPage(): JSX.Element {
  const [region, setRegion] = useState("EU");
  const [orgInput, setOrgInput] = useState("");
  const { org, pinned } = useTenantScope(orgInput);
  const [biomarkers, setBiomarkers] = useState("");
  const [match, setMatch] = useState<"all" | "any">("all");
  const [profile, setProfile] = useState("");
  const [q, setQ] = useState("");
  const [fuzzy, setFuzzy] = useState(true);

  const search = useMutation<SearchResponse, Error, void>({
    mutationFn: () =>
      searchAuthoring({
        region,
        org: org || undefined,
        biomarkers: biomarkers.trim() || undefined,
        biomarkerMatch: match,
        profile: profile.trim() || undefined,
        q: q.trim().length >= 2 ? q.trim() : undefined,
        fuzzy,
      }),
  });

  const result = search.data;
  const hasCriteria = biomarkers.trim() !== "" || profile.trim() !== "" || q.trim().length >= 2;

  return (
    <AppLayout breadcrumbs={[{ label: t("authoring.search_title") }]}>
      <Title order={1}>{t("authoring.search_title")}</Title>
      <Text c="dimmed" mt="xs">
        {t("authoring.search_intro")}
      </Text>
      <Stack mt="md" gap="xs">
        <Group align="flex-end">
          <Select
            label={t("authoring.region")}
            data={[...REGIONS]}
            value={region}
            allowDeselect={false}
            onChange={(v) => {
              if (v !== null) setRegion(v);
            }}
          />
          <TenantField org={org} pinned={pinned} onChange={setOrgInput} />
        </Group>
        <Group align="flex-end">
          <div style={{ flexGrow: 1 }}>
            <BiomarkerCombobox
              label={t("authoring.search_biomarkers")}
              aria-label={t("authoring.search_biomarkers")}
              placeholder="GLU, TCH"
              commaSeparated
              value={biomarkers}
              onChange={setBiomarkers}
            />
          </div>
          <Select
            label={t("authoring.search_match")}
            data={[
              { value: "all", label: t("authoring.search_match_all") },
              { value: "any", label: t("authoring.search_match_any") },
            ]}
            value={match}
            allowDeselect={false}
            onChange={(v) => {
              if (v === "all" || v === "any") setMatch(v);
            }}
          />
        </Group>
        <TextInput
          label={t("authoring.search_profile")}
          placeholder="diabetes, smoker"
          value={profile}
          onChange={(e) => setProfile(e.currentTarget.value)}
        />
        <TextInput
          label={t("authoring.search_text")}
          placeholder={t("authoring.search_text_placeholder")}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
        <Group>
          <Button
            loading={search.isPending}
            disabled={!hasCriteria}
            onClick={() => search.mutate()}
          >
            {t("authoring.search_run")}
          </Button>
          <Switch
            label={t("authoring.search_fuzzy")}
            checked={fuzzy}
            onChange={(e) => setFuzzy(e.currentTarget.checked)}
          />
          {!hasCriteria && (
            <Text size="sm" c="dimmed">
              {t("authoring.search_need_criteria")}
            </Text>
          )}
        </Group>
      </Stack>

      {search.isError && (
        <Alert color="red" mt="md" title={t("authoring.error_title")}>
          {search.error.message}
        </Alert>
      )}

      {result !== undefined && (
        <Stack mt="lg">
          {(Object.keys(result.resolved_biomarkers).length > 0 ||
            Object.keys(result.resolved_profile).length > 0) && (
            <Text size="sm" c="dimmed">
              {t("authoring.search_resolved_prefix")}{" "}
              {[
                ...Object.entries(result.resolved_biomarkers).map(
                  ([term, symbols]) => `${term} → ${symbols.join(", ")}`,
                ),
                ...Object.entries(result.resolved_profile).map(
                  ([term, variants]) => `${term} → ${variants.join(", ")}`,
                ),
              ].join(" · ")}
            </Text>
          )}
          <Title order={3}>
            {t("authoring.search_rules_heading")} ({result.total_rules})
          </Title>
          {result.rules.length === 0 ? (
            <Text c="dimmed">{t("authoring.rules_empty")}</Text>
          ) : (
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("authoring.col_insight")}</Table.Th>
                  <Table.Th>{t("authoring.col_type")}</Table.Th>
                  <Table.Th>{t("authoring.col_biomarkers")}</Table.Th>
                  <Table.Th>{t("authoring.col_conditions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.rules.map((rule) => (
                  <Table.Tr key={rule.insight}>
                    <Table.Td>
                      <Anchor
                        component={Link}
                        to={`/authoring/rules/${encodeURIComponent(region)}/${encodeURIComponent(
                          rule.insight,
                        )}${org ? `?org=${encodeURIComponent(org)}` : ""}`}
                      >
                        {rule.insight}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light">{rule.type}</Badge>
                    </Table.Td>
                    <Table.Td>{rule.related_biomarkers.join(", ")}</Table.Td>
                    <Table.Td>
                      {rule.conditions
                        .map((c) => `${c.attribute} ${c.operator} ${JSON.stringify(c.value)}`)
                        .join("; ")}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          <Title order={3}>
            {t("authoring.search_content_heading")} ({result.total_content})
          </Title>
          {result.content_truncated && (
            <Alert color="yellow">{t("authoring.search_truncated")}</Alert>
          )}
          {result.content.length === 0 ? (
            <Text c="dimmed">{t("authoring.content_empty")}</Text>
          ) : (
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("authoring.col_insight")}</Table.Th>
                  <Table.Th>{t("authoring.region")}</Table.Th>
                  <Table.Th>{t("authoring.lang")}</Table.Th>
                  <Table.Th>{t("authoring.search_col_field")}</Table.Th>
                  <Table.Th>{t("authoring.search_col_snippet")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.content.map((hit) => (
                  <Table.Tr key={`${hit.insight_id}:${hit.region}:${hit.lang}:${hit.field}`}>
                    <Table.Td>
                      <Anchor
                        component={Link}
                        to={`/authoring/content/${encodeURIComponent(
                          hit.insight_id.replace(/^#/, ""),
                        )}`}
                      >
                        {hit.insight_id}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>{hit.region}</Table.Td>
                    <Table.Td>{hit.lang}</Table.Td>
                    <Table.Td>{hit.field}</Table.Td>
                    <Table.Td>
                      <Text size="sm">{hit.snippet}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      )}
    </AppLayout>
  );
}
